// src/pages/Users/Techniques/hooks/useTechniquesController.js

import { useCallback, useEffect, useMemo, useState } from "react";

import { getAllTechniques } from "../../../../services/techniquesServices";

import {
  getUserFavorites,
  addFavorite,
  removeFavorite,
} from "../../../../services/usersTechniquesServices";

import {
  DEFAULT_TECHNIQUES_STATE,
  buildTechniqueOptions,
  filterAndSortTechniques,
  buildShareUrl,
} from "../utils/techniquesUtils";

const STORAGE_KEY = "techniques_ui_state";

function readStorageState() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return DEFAULT_TECHNIQUES_STATE;

    return {
      ...DEFAULT_TECHNIQUES_STATE,
      ...JSON.parse(saved),
    };
  } catch {
    return DEFAULT_TECHNIQUES_STATE;
  }
}

function readUserId() {
  try {
    const info = JSON.parse(localStorage.getItem("userInfo"));
    return info?.userId ?? null;
  } catch {
    return null;
  }
}

export default function useTechniquesController() {
  const [allTechniques, setAllTechniques] = useState([]);
  const [loading, setLoading] = useState(true);

  const [favorites, setFavorites] = useState([]);
  const [ui, setUi] = useState(readStorageState);
  const [searchInput, setSearchInput] = useState(ui.search || "");

  const [shareCopied, setShareCopied] = useState(false);

  const userId = useMemo(() => readUserId(), []);

  const persistUi = useCallback((nextState) => {
    setUi(nextState);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(nextState));
  }, []);

  const updateUi = useCallback((patch) => {
    setUi((prev) => {
      const next = {
        ...prev,
        ...patch,
        perPage:
          patch.perPage !== undefined
            ? patch.perPage
            : DEFAULT_TECHNIQUES_STATE.perPage,
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setUi((prev) => {
        if (prev.search === searchInput) return prev;

        const next = {
          ...prev,
          search: searchInput,
          perPage: DEFAULT_TECHNIQUES_STATE.perPage,
        };

        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        return next;
      });
    }, 250);

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    let mounted = true;

    async function fetchTechniques() {
      setLoading(true);

      try {
        const data = await getAllTechniques();

        if (!mounted) return;

        setAllTechniques(Array.isArray(data) ? data.filter(Boolean) : []);
      } catch (error) {
        console.error("Erreur chargement techniques :", error);

        if (mounted) {
          setAllTechniques([]);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    fetchTechniques();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    async function fetchFavorites() {
      if (!userId) return;

      try {
        const favs = await getUserFavorites(userId);

        if (!mounted) return;

        setFavorites(
          Array.isArray(favs) ? favs.map((fav) => fav.technique_id) : [],
        );
      } catch (error) {
        console.error("Erreur chargement favoris techniques :", error);
      }
    }

    fetchFavorites();

    return () => {
      mounted = false;
    };
  }, [userId]);

  const dynamicOptions = useMemo(
    () => buildTechniqueOptions(allTechniques),
    [allTechniques],
  );

  const filtered = useMemo(
    () => filterAndSortTechniques(allTechniques, ui, favorites),
    [allTechniques, ui, favorites],
  );

  const visible = useMemo(
    () => filtered.slice(0, ui.perPage),
    [filtered, ui.perPage],
  );

  const resetFilters = useCallback(() => {
    persistUi(DEFAULT_TECHNIQUES_STATE);
    setSearchInput(DEFAULT_TECHNIQUES_STATE.search);
  }, [persistUi]);

  const loadMore = useCallback(() => {
    updateUi({
      perPage: ui.perPage + DEFAULT_TECHNIQUES_STATE.perPage,
    });
  }, [ui.perPage, updateUi]);

  const toggleFavorite = useCallback(
    async (techniqueId) => {
      if (!userId || !techniqueId) return;

      const isAlreadyFavorite = favorites.includes(techniqueId);

      setFavorites((prev) =>
        isAlreadyFavorite
          ? prev.filter((id) => id !== techniqueId)
          : [...prev, techniqueId],
      );

      try {
        if (isAlreadyFavorite) {
          await removeFavorite(userId, techniqueId);
        } else {
          await addFavorite(userId, techniqueId);
        }
      } catch (error) {
        console.error("Erreur favori technique :", error);

        setFavorites((prev) =>
          isAlreadyFavorite
            ? [...prev, techniqueId]
            : prev.filter((id) => id !== techniqueId),
        );
      }
    },
    [favorites, userId],
  );

  const copyShareLink = useCallback(async () => {
    try {
      const url = buildShareUrl(ui);

      await navigator.clipboard.writeText(url);

      setShareCopied(true);

      setTimeout(() => {
        setShareCopied(false);
      }, 1600);
    } catch (error) {
      console.error("Erreur copie lien partage :", error);
    }
  }, [ui]);

  return {
    loading,
    allTechniques,
    filtered,
    visible,
    favorites,
    ui,
    searchInput,
    setSearchInput,
    dynamicOptions,
    updateUi,
    resetFilters,
    loadMore,
    toggleFavorite,
    copyShareLink,
    shareCopied,
  };
}
